import { useLandlordPendingAmendment } from '@services/amendments/queries/useRentAmendments'
import {
  previousPrices,
  PRICE_FIELDS,
  type Prices
} from '@services/amendments/types'
import { useOffersDetail } from '@services/offers/queries/useOffersDetail'
import { format, parseISO } from 'date-fns'
import { BellRing, TrendingUp } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'

import { money, round2 } from '../utils'
import { RentAmendmentDialog } from './RentAmendmentDialog'
import { RentAmendmentHistory } from './RentAmendmentHistory'

/**
 * Rent changes on an ONGOING offer: the "increase rent" action, the history
 * of the tenant's amendments and, when the landlord's rent went up and the
 * tenant has no amendment since, a prompt that opens the dialog with the
 * same difference already added.
 */
export function OfferRentAmendments({ offerId }: { offerId: string }) {
  const { t } = useTranslation('amendments')
  const { data: offer } = useOffersDetail<'ADMIN'>(offerId)
  const { data: landlordIncrease } = useLandlordPendingAmendment(offerId)
  const [open, setOpen] = useState(false)
  const [prefill, setPrefill] = useState<Prices | undefined>()

  if (!offer) return null

  const current = Object.fromEntries(
    PRICE_FIELDS.map((f) => [f, Number(offer[f] ?? 0)])
  ) as Prices

  const openWith = (values?: Prices) => {
    setPrefill(values)
    setOpen(true)
  }

  const withLandlordDifference = (): Prices | undefined => {
    if (!landlordIncrease) return undefined
    const before = previousPrices(landlordIncrease)
    return Object.fromEntries(
      PRICE_FIELDS.map((f) => [
        f,
        round2(current[f] + (landlordIncrease[f] || 0) - (before[f] || 0))
      ])
    ) as Prices
  }

  return (
    <div className="flex w-full flex-col items-start gap-3">
      {landlordIncrease && (
        <div className="w-full space-y-2 rounded-xl border border-peach/30 bg-peach/10 p-3 text-sm text-ink">
          <div className="flex items-start gap-2">
            <BellRing className="mt-0.5 h-4 w-4 shrink-0 text-peach" />
            <div>
              <div className="font-semibold">
                {t('landlord_increase_title')}
              </div>
              <div className="text-xs text-muted-foreground">
                {t('landlord_increase_body', {
                  month: format(
                    parseISO(landlordIncrease.effective_from),
                    'MM/yyyy'
                  ),
                  from: money(landlordIncrease.previous_rent),
                  to: money(landlordIncrease.rent),
                  interpolation: { escapeValue: false }
                })}
              </div>
            </div>
          </div>
          <Button size="sm" onClick={() => openWith(withLandlordDifference())}>
            {t('landlord_increase_action')}
          </Button>
        </div>
      )}

      <RentAmendmentHistory offerId={offerId} />

      <Button variant="outline" onClick={() => openWith(undefined)}>
        <TrendingUp className="h-4 w-4" />
        {t('increase_rent')}
      </Button>

      <RentAmendmentDialog
        side="TENANT"
        offerId={offerId}
        current={current}
        prefill={prefill}
        open={open}
        onOpenChange={setOpen}
      />
    </div>
  )
}
